import PageHeaderText from 'components/base/page-header-text';
import React, { useEffect, useState } from 'react';
import { Table, TableRow, TableData, Paginate } from '@cokitchen/cokitchen-components';
import StockFilters, { InventoryQuantityType } from 'pages/shared/inventory-stock/stock-filters';
import { ALL } from 'utils/constants';
import { apiService } from 'services/base.service';
import { handleApiError } from 'utils/functions';
import useDashboardContext from 'contexts/dashboard.context';
import ActivityIndicator from 'components/base/loader/activityIndicator';
import { WasteRecordType, WasteSummaryType } from 'models/waste-record.model';
import moment from 'moment';

const WasteReport = () => {
  const { activeStore } = useDashboardContext();
  const storeId = activeStore?.id || '';

  const [lastPage, setLastPage] = useState(1);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [quantityFilter, setQuantityFilter] = useState<InventoryQuantityType>(ALL);
  const [wasteData, setWasteData] = useState<WasteRecordType[]>([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [wasteSummary, setWasteSummary] = useState<WasteSummaryType>({} as WasteSummaryType);

  const getWasteReport = async () => {
    if (!storeId) return;
    setLoading(true);
    try {
      const params: { page: string; name: string; min_quantity_filter?: string } = {
        page: String(currentPage) || '1',
        name: searchValue
      };
      if (quantityFilter !== ALL) {
        params.min_quantity_filter = quantityFilter.toLowerCase();
      }
      const res = await apiService.get(`main-store/${storeId}/waste-records`, {
        version: 'v2',
        params
      });
      if (res?.data?.data) {
        setWasteData(res.data.data);
        setLastPage(Math.ceil((res?.data?.pagination?.total || 1) / 10));
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const getWasteSummary = async () => {
    if (!storeId) return;
    setSummaryLoading(true);
    try {
      const res = await apiService.get(`main-store/${storeId}/waste-summary`, {
        version: 'v2'
      });
      if (res?.data) {
        setWasteSummary(res.data.data ?? res.data);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setSummaryLoading(false);
    }
  };

  useEffect(() => {
    getWasteReport();
    getWasteSummary();
  }, [storeId, currentPage, searchValue, quantityFilter]);
  const TABLE_HEADERS = ['Item', 'Qty wasted(g)', 'Date reported', 'Note'];
  return (
    <div>
      <PageHeaderText
        title='Waste report (Main-Store)'
        description='Keep track of waste from the kitchen'></PageHeaderText>
      <StockFilters
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        quantityFilter={quantityFilter}
        setQuantityFilter={setQuantityFilter}
        showQuantityFilter={false}
        lastPage={1}
      />
      <div className='grid grid-cols-3 border-4 border-[#F7F7F8] mt-4 rounded py-9'>
        {summaryLoading ? (
          <div className='col-span-3 p-6'>
            <ActivityIndicator />
          </div>
        ) : (
          <>
            <div className='flex flex-col gap-[6px] items-center py-[7px]'>
              <div>
                <p className='text-gray-9 text-[10px] leading-[15px] tracking-[1px]'>
                  TOTAL ITEMS WASTED
                </p>
              </div>
              <div>
                <h4 className='text-[24px] leading-[28.8px] -tracking-[0.1px] fonnt-medium'>
                  {wasteSummary
                    ? Number(wasteSummary.total_items_wasted ?? 0).toLocaleString()
                    : '—'}
                </h4>
              </div>
            </div>
            <div className='flex flex-col gap-[6px] items-center py-[7px] border-l-[0.8px] border-[#EAEBEB]'>
              <div>
                <p className='text-gray-9 text-[10px] leading-[15px] tracking-[1px]'>
                  TOTAL % WASTED
                </p>
              </div>
              <div>
                <h4 className='text-[24px] leading-[28.8px] -tracking-[0.1px] fonnt-medium'>
                  {wasteSummary
                    ? `${Number(wasteSummary.total_percent_wasted ?? 0).toFixed(2)}%`
                    : '—'}
                </h4>
              </div>
            </div>
            <div className='flex flex-col gap-[6px] items-center py-[7px] border-l-[0.8px] border-[#EAEBEB]'>
              <div>
                <p className='text-gray-9 text-[10px] leading-[15px] tracking-[1px]'>TOP ITEM</p>
              </div>
              <div>
                <h4 className='text-[24px] leading-[28.8px] -tracking-[0.1px] fonnt-medium'>
                  {wasteSummary ? wasteSummary.top_item?.name ?? '—' : '—'}
                </h4>
              </div>
            </div>
          </>
        )}
      </div>
      <div className='border-4 border-[#F7F7F8] rounded mt-10'>
        <h4 className='text-[#2E3031] text-[18px] font-medium px-6 pt-[18px] pb-4'>
          Waste history
        </h4>
        {loading ? (
          <div className='p-6'>
            <ActivityIndicator />
          </div>
        ) : wasteData.length === 0 ? (
          <div className='p-10 flex flex-col items-center justify-center gap-3'>
            <div className='text-6xl'>🗃️</div>
            <p className='text-gray-7'>No waste records yet</p>
          </div>
        ) : (
          <Table headers={TABLE_HEADERS}>
            {wasteData.map((wasteItem, idx) => (
              <TableRow key={idx} className='hover:bg-[#F5F6F6] table_row'>
                <TableData>{wasteItem?.ingredient.name}</TableData>
                <TableData>{wasteItem?.quantity}</TableData>
                <TableData>
                  {moment(wasteItem?.created_at)?.format('Do MMM, YYYY [at] h:mm A')}
                </TableData>
                <TableData>{wasteItem?.note}</TableData>
              </TableRow>
            ))}
          </Table>
        )}
      </div>
      <Paginate
        pageCount={lastPage}
        pageRangeDisplayed={2}
        onPageChange={(e) => {
          setCurrentPage(e.selected + 1);
        }}
      />
    </div>
  );
};

export default WasteReport;
